import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import SubscriptionDetailModal from './SubscriptionDetailModal';

const AST_FONT = "'abcNormal','Inter','Arial',sans-serif";
const AST_FONT_FACE = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  /* .ast-table forces real table layout on mobile — the global
     table{display:block!important} rule (App.css) breaks this table's
     columns and pushes "Editar plan" out of reach; a class selector beats
     that rule at equal !important specificity, and the wrapper's own
     overflow-x already gives mobile a normal horizontal-scroll table. */
  .ast-table{display:table !important}
  /* Acciones pinned to the right edge of the scroll container — on a
     narrow window (mobile, or just a non-maximized desktop browser) this
     table is wider than its box, and "Editar plan" sat past the fold at
     the far right where nothing hints there's more to scroll to. Sticky
     keeps it always reachable regardless of scroll position. */
  .ast-sticky{position:sticky;right:0;background:#fff;box-shadow:-6px 0 8px -6px rgba(0,0,0,.08)}
  thead .ast-sticky{background:#F8FAFC;box-shadow:none}
  tbody tr:hover .ast-sticky{background:#F9FAFB}
`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

const MetricCard = ({ label, value, accent }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</div>
    <div className="mt-1 text-2xl font-semibold" style={{ color: accent || '#1E293B' }}>{value}</div>
  </div>
);

const PlanBadge = ({ planId, expired }) => {
  if (expired) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold" style={{ background: '#FEF2F2', color: '#B91C1C' }}>
        Pro · Expirado
      </span>
    );
  }
  if (planId === 'enterprise') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold" style={{ background: 'rgba(124, 58, 237, 0.10)', color: '#7C3AED' }}>
        Enterprise
      </span>
    );
  }
  if (planId === 'pro') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold" style={{ background: 'rgba(0, 74, 173, 0.10)', color: '#004AAD' }}>
        Pro
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold" style={{ background: '#F1F5F9', color: '#475569' }}>
      Free
    </span>
  );
};

// Real MercadoPago billing state, separate from PlanBadge above — a row
// can be "Pro" (the local entitlement PlanBadge shows) with no real
// subscription behind it at all (an admin-granted trial), or vice versa
// mid-grace-period (status already "cancelled" on MercadoPago's side but
// PlanBadge still shows "Pro" until access_until passes). Both badges
// together are what make that distinction visible in the table, where
// previously only the local entitlement was shown.
const SUB_STATUS_META = {
  authorized: { label: 'Activa', bg: '#ECFDF5', color: '#059669' },
  paused: { label: 'Pausada', bg: '#FFFBEB', color: '#B45309' },
  cancelled: { label: 'Cancelada', bg: '#FEF2F2', color: '#B91C1C' },
  pending: { label: 'Pendiente', bg: '#F1F5F9', color: '#475569' },
};

const SubscriptionStatusBadge = ({ status, atRisk }) => {
  if (!status) {
    return <span className="text-xs text-[#CBD5E1]">Sin suscripción</span>;
  }
  const meta = SUB_STATUS_META[status] || { label: status, bg: '#F1F5F9', color: '#475569' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold" style={{ background: atRisk ? '#FEF2F2' : meta.bg, color: atRisk ? '#B91C1C' : meta.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: atRisk ? '#B91C1C' : meta.color }} />
      {atRisk ? 'En riesgo' : meta.label}
    </span>
  );
};

// Compact month-over-month revenue trend — deliberately plain CSS bars
// (height via inline %) rather than reusing EnhancedLineChart.js: that
// component's sizing/margin logic is tailored to its own fixed 4-metric
// shape and axis conventions, and forcing a different dataset
// (revenue_clp/new_count/cancelled_count per month) through it risks
// re-introducing the exact margin/overflow issues already fixed there
// for its real use case. This one has no axes to get wrong.
const RevenueTrendChart = ({ trend }) => {
  if (!trend || trend.length === 0) return null;
  const max = Math.max(...trend.map((m) => m.revenue_clp || 0), 1);
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Ingresos por mes (últimos 12 meses)</p>
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
        <div className="flex items-end gap-1.5" style={{ height: 120 }}>
          {trend.map((m) => {
            const h = Math.max(4, Math.round((m.revenue_clp / max) * 100));
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                <div
                  title={`${m.month}: $${Number(m.revenue_clp || 0).toLocaleString('es-CL')} CLP`}
                  className="w-full rounded-t transition-all"
                  style={{ height: `${h}%`, background: '#004AAD', opacity: 0.85, minHeight: 2 }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex gap-1.5 mt-1.5">
          {trend.map((m) => (
            <div key={m.month} className="flex-1 text-center text-[10px] text-[#94A3B8]">
              {m.month.slice(5)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EditPlanModal = ({ user, onClose, onSaved }) => {
  const [plan, setPlan] = useState(user.plan_id || 'free');
  const [maxProfiles, setMaxProfiles] = useState(user.max_profiles || 1);
  const [expiresAt, setExpiresAt] = useState(user.expires_at ? user.expires_at.slice(0, 10) : '');
  const [active, setActive] = useState(user.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [features, setFeatures] = useState([]);
  const [plansCatalog, setPlansCatalog] = useState([]);
  const [overrides, setOverrides] = useState(user.feature_overrides || []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/features`),
      axios.get(`${API}/plans/catalog`),
    ]).then(([f, p]) => {
      setFeatures(f.data.features || []);
      setPlansCatalog(p.data.plans || []);
    }).catch(() => {});
  }, []);

  const planFeatures = plansCatalog.find((p) => p.id === plan)?.features || [];
  const toggleOverride = (key) => {
    setOverrides((prev) => (prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { plan, active };
      if (plan === 'pro' || plan === 'enterprise') {
        payload.max_profiles = Number(maxProfiles) || 1;
        payload.expires_at = expiresAt ? new Date(expiresAt).toISOString() : null;
      }
      // Overrides already covered by the newly selected plan are redundant —
      // strip them so the stored list only reflects real exceptions.
      const cleanOverrides = overrides.filter((f) => !planFeatures.includes(f));
      await Promise.all([
        axios.put(`${API}/admin/users/${user.id}/plan`, payload),
        axios.put(`${API}/admin/users/${user.id}/feature-overrides`, { features: cleanOverrides }),
      ]);
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-edit-plan-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-[#1E293B]">Editar suscripción</h3>
          <p className="text-sm text-[#64748B] mt-0.5">{user.email}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Plan</label>
            <div className="grid grid-cols-3 gap-2">
              {['free', 'pro', 'enterprise'].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`plan-option-${p}`}
                  onClick={() => setPlan(p)}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${
                    plan === p ? 'border-[#004AAD] bg-[#004AAD]/5 text-[#004AAD]' : 'border-gray-200 text-[#64748B] hover:border-gray-300'
                  }`}
                >
                  {p === 'free' ? 'Free' : p === 'pro' ? 'Pro' : 'Enterprise'}
                </button>
              ))}
            </div>
          </div>

          {(plan === 'pro' || plan === 'enterprise') && (
            <>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Max. perfiles</label>
                <input
                  data-testid="admin-max-profiles"
                  type="number"
                  min="1"
                  value={maxProfiles}
                  onChange={(e) => setMaxProfiles(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Fecha de expiración (opcional)</label>
                <input
                  data-testid="admin-expires-at"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm"
                />
                <p className="text-xs text-[#94A3B8] mt-1">Dejar vacío = sin expiración</p>
              </div>
            </>
          )}

          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <div className="text-sm font-medium text-[#1E293B]">Cuenta activa</div>
              <div className="text-xs text-[#64748B]">Desactivar bloquea el acceso al login</div>
            </div>
            <button
              data-testid="admin-toggle-active"
              type="button"
              onClick={() => setActive(!active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${active ? 'bg-[#004AAD]' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="border-t pt-4">
            <div className="text-sm font-medium text-[#1E293B]">Permisos adicionales</div>
            <div className="text-xs text-[#64748B] mb-2">Habilita funciones sueltas sin cambiar el plan</div>
            <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
              {features.map((f) => {
                const includedByPlan = planFeatures.includes(f.key);
                const checked = includedByPlan || overrides.includes(f.key);
                return (
                  <label
                    key={f.key}
                    data-testid={`admin-feature-${f.key}`}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm ${includedByPlan ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={includedByPlan}
                      onChange={() => toggleOverride(f.key)}
                      className="rounded border-gray-300 text-[#004AAD] focus:ring-[#004AAD]/20"
                    />
                    <span className="flex-1 text-[#1E293B]">{f.label}</span>
                    {includedByPlan && <span className="text-xs text-[#94A3B8]">Incluido en el plan</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="rounded-lg px-3 py-2 text-sm" style={{ background: '#FEF2F2', color: '#B91C1C' }}>{error}</div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              data-testid="admin-save-plan"
              onClick={save}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all hover:shadow disabled:opacity-50"
              style={{ background: '#004AAD' }}
            >
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg font-medium text-[#64748B] hover:bg-gray-100 transition"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminSubscriptionsTab = () => {
  const { impersonate } = useAuth();
  const { confirm, toast } = useNotification();
  const [metrics, setMetrics] = useState(null);
  const [billingMetrics, setBillingMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [managingSub, setManagingSub] = useState(null);
  const [quickFilter, setQuickFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  const limit = 25;

  const viewAsUser = async (u) => {
    const ok = await confirm(
      `Vas a entrar al dashboard como ${u.email}. Podrás volver a tu cuenta de admin en cualquier momento.`,
      { confirmLabel: 'Ver como este usuario' }
    );
    if (!ok) return;
    const result = await impersonate(u.id);
    if (result.success) {
      window.location.href = '/dashboard';
    } else {
      toast.error(result.error || 'No se pudo iniciar sesión como este usuario');
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/subscriptions/export`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `suscripciones_contact_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado.');
    } catch (e) {
      toast.error('No se pudo exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (filter) params.plan = filter;
      if (search) params.search = search;
      const [m, list] = await Promise.all([
        axios.get(`${API}/admin/subscriptions/metrics`),
        axios.get(`${API}/admin/subscriptions`, { params }),
      ]);
      setMetrics(m.data);
      setUsers(list.data.users || []);
      setTotal(list.data.total || 0);
      // Independent, best-effort fetch — a failure here shouldn't block
      // the user list (the main content of this tab) from loading.
      axios.get(`${API}/admin/subscriptions/billing-metrics`)
        .then((bm) => setBillingMetrics(bm.data))
        .catch((e) => console.error('Error loading billing metrics:', e));
    } catch (e) {
      console.error('Error loading subscriptions:', e);
    } finally {
      setLoading(false);
    }
  }, [filter, search, skip]);

  useEffect(() => {
    load();
  }, [load]);

  const displayedUsers = users.filter((u) => {
    if (quickFilter === 'all') return true;
    if (quickFilter === 'at_risk') return u.subscription_status === 'authorized' && u.last_payment_status === 'rejected';
    if (quickFilter === 'paused') return u.subscription_status === 'paused';
    if (quickFilter === 'cancelled') return u.subscription_status === 'cancelled';
    return true;
  });

  return (
    <div className="w-full space-y-6" data-testid="admin-subscriptions-tab">
      <style>{AST_FONT_FACE}</style>
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', fontFamily: AST_FONT }}>Suscripciones</h1>
        <p className="text-sm text-[#64748B] mt-1">Gestiona los planes y permisos de todos los usuarios.</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricCard label="Usuarios totales" value={metrics?.total_users ?? '—'} />
        <MetricCard label="Usuarios Free" value={metrics?.free_users ?? '—'} />
        <MetricCard label="Usuarios Pro" value={metrics?.pro_users ?? '—'} accent="#004AAD" />
        <MetricCard label="Conversión" value={metrics ? `${metrics.conversion_rate}%` : '—'} />
        <MetricCard label="Pro expirados" value={metrics?.expired_pro ?? '—'} accent="#B91C1C" />
      </div>

      {/* Billing metrics — real MercadoPago subscription health, separate
          from the user/plan-count metrics above. */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Facturación</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard
            label="MRR estimado"
            value={billingMetrics ? `$${Number(billingMetrics.mrr_clp || 0).toLocaleString('es-CL')}` : '—'}
            accent="#059669"
          />
          <MetricCard label="Suscripciones activas" value={billingMetrics?.active_subscriptions ?? '—'} accent="#004AAD" />
          <MetricCard label="En riesgo" value={billingMetrics?.at_risk_subscriptions ?? '—'} accent="#B91C1C" />
          <MetricCard label="Pausadas" value={billingMetrics?.paused_subscriptions ?? '—'} />
          <MetricCard label="Nuevas este mes" value={billingMetrics?.new_this_month ?? '—'} accent="#059669" />
          <MetricCard label="Canceladas este mes" value={billingMetrics?.cancelled_this_month ?? '—'} accent="#B91C1C" />
        </div>
      </div>

      <RevenueTrendChart trend={billingMetrics?.mrr_trend} />

      {/* Quick status filters — client-side, over the already-fetched
          page (the plan <select> below still drives the real paginated
          query; these just narrow what's visible on the current page by
          real MercadoPago status, since that's not something the backend
          query filters on today). */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'Todas' },
          { id: 'at_risk', label: 'En riesgo' },
          { id: 'paused', label: 'Pausadas' },
          { id: 'cancelled', label: 'Canceladas' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setQuickFilter(f.id)}
            className="px-3 py-1.5 text-xs font-medium rounded-full border transition"
            style={quickFilter === f.id
              ? { background: '#004AAD', borderColor: '#004AAD', color: '#fff' }
              : { background: '#fff', borderColor: '#E2E8F0', color: '#64748B' }}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={exportExcel}
          disabled={exporting}
          className="ml-auto px-3 py-1.5 text-xs font-medium rounded-full border transition disabled:opacity-50"
          style={{ background: '#fff', borderColor: '#E2E8F0', color: '#475569' }}
        >
          {exporting ? 'Exportando…' : '📊 Exportar Excel'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          data-testid="admin-subs-search"
          placeholder="Buscar por email o nombre"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm"
        />
        <select
          data-testid="admin-subs-filter"
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setSkip(0); }}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 text-sm bg-white"
        >
          <option value="">Todos los planes</option>
          <option value="free">Solo Free</option>
          <option value="pro">Solo Pro</option>
          <option value="enterprise">Solo Enterprise</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto" style={{WebkitOverflowScrolling:'touch'}}>
          <table className="ast-table w-full text-sm">
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Usuario</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Plan</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">MercadoPago</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Perfiles</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Registro</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Expira</th>
                <th className="text-left px-4 py-3 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Estado</th>
                <th className="text-right px-4 py-3 ast-sticky"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-12 text-[#64748B]">Cargando…</td></tr>
              ) : displayedUsers.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12 text-[#64748B]">No hay usuarios que coincidan con este filtro.</td></tr>
              ) : displayedUsers.map((u) => (
                <tr key={u.id} data-testid={`admin-subs-row-${u.id}`} className="border-t border-gray-100 hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1E293B]">{u.name || '—'}</div>
                    <div className="text-xs text-[#64748B]">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <PlanBadge planId={u.plan_id} expired={u.status === 'expired'} />
                      {u.feature_overrides?.length > 0 && (
                        <span
                          title={u.feature_overrides.join(', ')}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: '#FFFBEB', color: '#B45309' }}
                        >
                          +{u.feature_overrides.length} permiso{u.feature_overrides.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <SubscriptionStatusBadge
                      status={u.subscription_status}
                      atRisk={u.subscription_status === 'authorized' && u.last_payment_status === 'rejected'}
                    />
                  </td>
                  <td className="px-4 py-3 text-[#1E293B]">
                    {u.profiles_used} / {u.max_profiles}
                  </td>
                  <td className="px-4 py-3 text-[#64748B]">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-[#64748B]">{formatDate(u.expires_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                      u.is_active ? 'text-green-700' : 'text-red-700'
                    }`} style={{ background: u.is_active ? '#ECFDF5' : '#FEF2F2' }}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                      {u.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right ast-sticky">
                    <div className="flex justify-end gap-2">
                      {!u.is_admin && (
                        <button
                          data-testid={`admin-view-as-${u.id}`}
                          onClick={() => viewAsUser(u)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg transition"
                          style={{ background: '#F1F5F9', color: '#475569' }}
                        >
                          Ver como usuario
                        </button>
                      )}
                      <button
                        data-testid={`admin-edit-${u.id}`}
                        onClick={() => setEditing(u)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg transition"
                        style={{ background: 'rgba(0, 74, 173, 0.08)', color: '#004AAD' }}
                      >
                        Editar plan
                      </button>
                      {u.subscription_id && (
                        <button
                          data-testid={`admin-manage-sub-${u.id}`}
                          onClick={() => setManagingSub(u)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg transition whitespace-nowrap"
                          style={{ background: '#ECFDF5', color: '#059669' }}
                        >
                          Gestionar suscripción
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-[#64748B]">
            <span>Mostrando {skip + 1}–{Math.min(skip + limit, total)} de {total}</span>
            <div className="flex gap-2">
              <button
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - limit))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                Anterior
              </button>
              <button
                disabled={skip + limit >= total}
                onClick={() => setSkip(skip + limit)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <EditPlanModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      {managingSub && (
        <SubscriptionDetailModal
          user={managingSub}
          onClose={() => setManagingSub(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default AdminSubscriptionsTab;
